import { useEffect, useRef, useState } from "react";
import CustomSelector from "../../shared/customSelector";
import { get } from "lodash/fp";
import {
  Modal,
  Form,
  Container,
  Content,
  Checkbox,
  Button,
  Schema,
  Loader,
} from "rsuite";
import { StringType, NumberType } from "schema-typed";
import { HelperServices } from "../../../core/services/HelperServices";
import { UserServices } from "../../../core/services/UserServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { useSelector } from "react-redux";
import {
  httpStatusArr,
  onOffArr,
  messageTypeArr,
  statusArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";

const AddAddressModal = ({
  status,
  toggle,
  isEditAddress,
  editAddObj,
  setIsEditAddress,
  setIsAddressChanged,
  setEditAddObj,
}: {
  status: boolean;
  toggle: any;
  isEditAddress: boolean;
  editAddObj: any;
  setIsEditAddress: any;
  setIsAddressChanged: any;
  setEditAddObj: any;
}) => {
  const { t } = useTranslation();
  const userActionData = useSelector((state: any) => {
    return state?.actionData?.id;
  });
  const formRef = useRef<any>();
  let editCasePrepopulate = {
    house_no: editAddObj?.house_no,
    landmark: editAddObj?.landmark,
    mobile: editAddObj?.mobile,
    pincode: editAddObj?.pincode,
  };
  const [editCaseValue, setEditCaseValue] = useState<any>(editCasePrepopulate); //review comment:if this should be removed from state as we are not setting any state for this. TP
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [formError, setFormError] = useState<any>({});
  const [formData, setFormData] = useState({});
  const [loader, setLoader] = useState(false);
  const [areas, setAreas] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const countryList = useSelector((state: any) => state.country.data);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  const [is_primary_address, setIsPrimaryAddress] = useState(
    editAddObj?.is_primary_address === onOffArr.on ? true : false
  );
  const [countryId, setCountryId] = useState(
    editAddObj?.country_id ? editAddObj?.country_id : ""
  );
  const [stateId, setStateId] = useState(
    editAddObj?.state_id ? editAddObj?.state_id : ""
  );
  const [cityId, setCityId] = useState(
    editAddObj?.city_id ? editAddObj?.city_id : ""
  );
  const [areaInfo, setAreaInfo] = useState<{ id: number; area_name: string }>({
    id: 0,
    area_name: editAddObj?.area_name ? editAddObj?.area_name : "",
  });
  const [buildingInfo, setBuildingInfo] = useState<{
    id: number;
    building_name: string;
  }>({
    id: 0,
    building_name: editAddObj?.building_name ? editAddObj?.building_name : "",
  });

  const formModel = Schema.Model({
    house_no: StringType().isRequired(
      t("REQUIRED", {
        field: t("HOUSE_NO").toLowerCase(),
      })
    ),
    landmark: StringType(),
    mobile: StringType()
      .isRequired(
        t("REQUIRED", {
          field: t("MOBILE").toLowerCase(),
        })
      )
      .pattern(
        /^\d{10}$/,
        t("VALID", {
          entity: t("MOBILE").toLowerCase(),
        })
      ),
    pincode: NumberType().isRequired(
      t("REQUIRED", {
        field: t("PINCODE").toLowerCase(),
      })
    ),
  });

  async function getStateList(countryId: number) {
    try {
      const stateRes = await HelperServices.getAllValidStates({
        country: countryId,
      });
      setStateList(get("data.data", stateRes));
    } catch (error) {}
  }

  async function getCityList(stateId: number) {
    try {
      const res = await HelperServices.getAllValidCities({ state: stateId });
      if (res?.data?.data?.length) {
        setCityList(res.data.data);
      }
    } catch (error) {}
  }

  async function searchArea(search: string, cityId: number) {
    try {
      if (search.length > 2) {
        const res = await HelperServices.getAllValidAreas({
          city: cityId,
          smart_search: search,
        });
        if (res?.data?.data?.length) {
          setAreas(res.data.data);
        }
      }
    } catch (error) {}
  }

  async function searchBuilding(search: string, cityId: number) {
    try {
      if (search.length > 2) {
        const res = await HelperServices.getBuildings({
          city: +cityId,
          smart_search: search,
          status: statusArr.active,
        });
        if (res?.data?.data?.length) {
          setBuildings(res.data.data);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function createAddress(payload: any) {
    try {
      setLoader(true);
      const res: any = await UserServices.createAddress(payload);
      if (res && res?.data?.status === httpStatusArr.ok) {
        setLoader(false);
        UtilityServices.showToaster({
          message: res?.data?.message,
        });
        toggle("");
      }
    } catch (error: any) {
      setLoader(false);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function updateAddress(addressId: number, payload: any) {
    try {
      setLoader(true);
      const res: any = await UserServices.updateAddress(addressId, payload);
      if (res && res?.data?.status === httpStatusArr.ok) {
        setLoader(false);
        UtilityServices.showToaster({
          message: res?.data?.message,
        });
        setIsAddressChanged(true);
        toggle("view-address");
      }
    } catch (error: any) {
      setLoader(false);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    }
  }

  const addAddressSubmitted = () => {
    let formFields: any = { ...formData };
    let payload = {
      user_id: userActionData,
      house_no: formFields["house_no"],
      landmark: formFields["landmark"],
      building: buildingInfo.building_name,
      area_name: areaInfo.area_name,
      city_id: cityId,
      state_id: stateId,
      country_id: countryId,
      pincode: formFields["pincode"],
      mobile: formFields["mobile"],
      is_primary_address: is_primary_address ? onOffArr.on : onOffArr.off,
    };
    if (isEditAddress) {
      updateAddress(+editAddObj.id, payload);
    } else {
      createAddress(payload);
    }
  };

  useEffect(() => {
    if (isEditAddress) {
      getStateList(editAddObj?.country_id);
      getCityList(editAddObj?.state_id);
    }
  }, []);

  return (
    <div className="modal-container">
      <Modal
        open={status}
        backdrop={true}
        onClose={(e: any) => {
          toggle("");
          setEditAddObj({});
          setIsEditAddress(false);
        }}
      >
        {loader && (
          <Loader
            style={{ zIndex: 9999 }}
            size="lg"
            backdrop
            content={t("LOADING")}
            vertical
          />
        )}
        <Modal.Header>
          <h4>
            {isEditAddress ? t("EDIT") : t("ADD")} {t("ADDRESS")}{" "}
          </h4>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Content className="edit-cnt add-cnt content">
              <Form
                layout="inline"
                ref={formRef}
                model={formModel}
                formError={formError}
                formDefaultValue={
                  Object.keys(editCaseValue).length ? editCaseValue : null
                }
                onCheck={(e: any) => {
                  setFormError(e);
                }}
                onSubmit={(e: any) => {
                  if (
                    e &&
                    countryId !== "" &&
                    stateId !== "" &&
                    cityId !== "" &&
                    areaInfo.area_name !== "" &&
                    buildingInfo.building_name !== ""
                  ) {
                    addAddressSubmitted();
                  }
                }}
                onChange={(data: any) => {
                  setFormData(data);
                }}
              >
                <Form.Group>
                  <Form.ControlLabel>{t("HOUSE_NO")}</Form.ControlLabel>
                  <Form.Control name="house_no" />
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>{t("LANDMARK")}</Form.ControlLabel>
                  <Form.Control name="landmark" />
                </Form.Group>

                <Form.Group>
                  <CustomSelector
                    loaderMsg={t("LOADING")}
                    valueKey="id"
                    labelKey="country_name"
                    isDisable={!countryList.length ? true : false}
                    labelName={t("COUNTRY")}
                    defaultValue={countryId}
                    customErrorMsg={
                      isFormSubmitted && countryId === ""
                        ? t("REQUIRED", {
                            field: t("COUNTRY").toLowerCase(),
                          })
                        : null
                    }
                    onClear={(event: any) => {
                      setCountryId("");
                      setStateId("");
                      setStateList([]);
                      setCityId("");
                      setCityList([]);
                    }}
                    listData={countryList.length ? countryList : []}
                    onSelect={(e: any, selectedObj: any) => {
                      setCountryId(e);
                      setStateList([]);
                      getStateList(+e);
                    }}
                  />
                </Form.Group>

                <Form.Group>
                  <CustomSelector
                    loaderMsg={t("LOADING")}
                    valueKey="id"
                    labelKey="state_name"
                    isDisable={!countryId || !stateList.length ? true : false}
                    labelName={t("STATE")}
                    defaultValue={stateId ? stateId : null}
                    customErrorMsg={
                      isFormSubmitted && stateId === ""
                        ? t("REQUIRED", {
                            field: t("STATE").toLowerCase(),
                          })
                        : null
                    }
                    onClear={(event: any) => {
                      setStateId("");
                      setCityId("");
                      setCityList([]);
                    }}
                    listData={
                      stateList.length
                        ? stateList
                        : countryId === ""
                        ? [{}]
                        : []
                    }
                    onSelect={(e: any, selectedObj: any) => {
                      setStateId(e);
                      setCityId("");
                      setCityList([]);
                      getCityList(+e);
                    }}
                  />
                </Form.Group>

                <Form.Group>
                  <CustomSelector
                    loaderMsg={t("LOADING")}
                    valueKey="id"
                    labelKey="city_name"
                    isDisable={
                      !countryId || !stateId || !cityList.length ? true : false
                    }
                    labelName={t("CITY")}
                    defaultValue={cityId ? cityId : ""}
                    customErrorMsg={
                      isFormSubmitted && cityId === ""
                        ? t("REQUIRED", {
                            field: t("CITY").toLowerCase(),
                          })
                        : null
                    }
                    onClear={(event: any) => {
                      setCityId("");
                      setAreas([]);
                      setAreaInfo({ id: 0, area_name: "" });
                      setBuildingInfo({ id: 0, building_name: "" });
                    }}
                    listData={
                      cityList.length
                        ? cityList
                        : stateId && countryId
                        ? []
                        : [{}]
                    }
                    onSelect={(e: any, selectedObj: any) => {
                      setCityId(e);
                      setAreaInfo({ id: 0, area_name: "" });
                      setBuildingInfo({ id: 0, building_name: "" });
                      setAreas([]);
                      setBuildings([]);
                    }}
                  />
                </Form.Group>

                <Form.Group className="form-field-bx">
                  <Form.ControlLabel> {t("AREA")} </Form.ControlLabel>
                  <Form.Control
                    disabled={
                      countryId === "" || stateId === "" || cityId === ""
                    }
                    name="searchArea"
                    value={areaInfo.area_name}
                    onChange={(value: string) => {
                      searchArea(value, +cityId);
                      setAreaInfo({ id: 0, area_name: value });
                    }}
                  />
                  {!!!areaInfo.area_name && isFormSubmitted && (
                    <>
                      <br />
                      <span style={{ color: "red" }}>
                        {t("REQUIRED", {
                          field: t("AREA").toLowerCase(),
                        })}
                      </span>
                    </>
                  )}

                  {areas && areas.length ? (
                    <div className="tarea-nm-bx">
                      {!areas?.length && (
                        <Loader size="sm" content={t("LOADING")} />
                      )}
                      {areas &&
                        areas.length &&
                        areas.map((area: any, index) => {
                          return (
                            <>
                              <span
                                className="tarea-nm-lbl"
                                key={index}
                                onClick={() => {
                                  setAreaInfo({
                                    id: area.id,
                                    area_name: area.area_name,
                                  });
                                  setAreas([]);
                                }}
                              >
                                {area && area.area_name}
                              </span>{" "}
                              <br />
                            </>
                          );
                        })}
                    </div>
                  ) : null}
                </Form.Group>

                <Form.Group className="form-field-bx">
                  <Form.ControlLabel>{t("BUILDING")} </Form.ControlLabel>
                  <Form.Control
                    disabled={
                      countryId === "" || stateId === "" || cityId === ""
                    }
                    name="searchBuilding"
                    value={buildingInfo.building_name}
                    onChange={(value: string) => {
                      searchBuilding(value, +cityId);
                      setBuildingInfo({ id: 0, building_name: value });
                    }}
                  />
                  {!!!buildingInfo.building_name && isFormSubmitted && (
                    <>
                      <br />
                      <span style={{ color: "red" }}>
                        {t("REQUIRED", {
                          field: t("BUILDING").toLowerCase(),
                        })}
                      </span>
                    </>
                  )}

                  {buildings && buildings.length ? (
                    <div className="tarea-nm-bx">
                      {!buildings?.length && (
                        <Loader size="sm" content={t("LOADING")} />
                      )}
                      {buildings.length &&
                        buildings.map((building: any, index) => {
                          return (
                            <>
                              <span
                                className="tarea-nm-lbl"
                                key={index}
                                onClick={() => {
                                  setBuildingInfo({
                                    id: building.id,
                                    building_name: building.building_name,
                                  });
                                  setBuildings([]);
                                }}
                              >
                                {building && building.building_name}
                              </span>{" "}
                              <br />
                            </>
                          );
                        })}
                    </div>
                  ) : null}
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>{t("MOBILE")} </Form.ControlLabel>
                  <Form.Control name="mobile" />
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>{t("PINCODE")} </Form.ControlLabel>
                  <Form.Control name="pincode" />
                </Form.Group>

                <Form.Group>
                  {t("IS_DEFAULT")}
                  <Checkbox
                    defaultChecked={is_primary_address}
                    onChange={(e: any) => {
                      setIsPrimaryAddress(!is_primary_address);
                    }}
                  ></Checkbox>
                </Form.Group>
                <div className="usr-form-btn">
                  <Form.Group>
                    <Button
                      appearance="primary"
                      disabled={
                        !countryList.length ||
                        !stateList.length ||
                        !cityList.length
                          ? true
                          : false
                      }
                      onClick={(e: any) => setIsFormSubmitted(true)}
                      type="submit"
                    >
                      {isEditAddress ? t("UPDATE") : t("ADD")} {t("ADDRESS")}
                    </Button>
                    <Button
                      style={{ marginLeft: "10px" }}
                      onClick={(e: any) => {
                        setIsFormSubmitted(false);
                        toggle("");
                      }}
                      appearance="primary"
                      color="red"
                      type="reset"
                    >
                      {t("CANCEL")}
                    </Button>
                  </Form.Group>
                </div>
              </Form>
            </Content>
          </Container>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AddAddressModal;
